import { useState } from 'react'
import { Button } from 'blaze-ui/Button'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { BrandPlanTactic } from 'types'
import { ContentCategory } from 'utils/brandPlans/contentCategories'
import {
  EMAIL_TACTICS,
  SEO_TACTICS,
  SOCIAL_MEDIA_TACTICS,
  STORY_TACTICS,
  VIDEO_TACTICS,
} from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'
import { TacticOptions } from 'screens/Schedule/FrequencySection/EditContentAccountsModal/TacticOptions'
import { ConnectedAccountsList } from 'screens/Schedule/FrequencySection/EditContentAccountsModal/ConnectedAccountsList'
import Styles from './AccountsDropdown.module.scss'
import cx from 'classnames'

export const getContentTypeTactics = (contentType: ContentCategory): BrandPlanTactic[] => {
  switch (contentType) {
    case 'still_image_post':
    case 'carousel_post':
    case 'feed_video_post':
      return [...SOCIAL_MEDIA_TACTICS]
    case 'stories':
      return [...STORY_TACTICS]
    case 'blog_post':
      return [...SEO_TACTICS]
    case 'newsletter':
      return [...EMAIL_TACTICS]
    case 'short_form_video':
      return [...VIDEO_TACTICS]
    default:
      return []
  }
}

interface AccountsDropdownProps {
  contentCategory: ContentCategory
  selectedIds: number[]
  onChange: (ids: number[]) => void
  isDisabled?: boolean
}

export const AccountsDropdown = ({ contentCategory, selectedIds, onChange, isDisabled }: AccountsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const tactics = getContentTypeTactics(contentCategory)

  const { data } = useExternalIntegrations({
    select: (data) => ({
      external_integrations: data.external_integrations.filter((i) => selectedIds.includes(i.id)),
    }),
  })
  const selectedAccounts = data?.external_integrations ?? []

  return (
    <DropdownTrigger placement="bottom-start" onOpenChange={setIsOpen}>
      <Button
        variant="ghost"
        size="md"
        withChevron={isOpen ? 'up' : 'down'}
        aria-label={`Select accounts for ${contentCategory}`}
        className={cx(Styles.accountsDropdownButton, { [Styles.empty]: selectedAccounts.length < 1 })}
        isDisabled={isDisabled}
      >
        {selectedAccounts.length > 0 && (
          <ConnectedAccountsList accounts={selectedAccounts} className={Styles.accountsList} />
        )}
        {selectedAccounts.length < 1 && 'Select accounts'}
      </Button>
      {(close) => (
        <ul className={Styles.tacticsList}>
          {tactics.map((tactic) => (
            <li key={tactic}>
              <TacticOptions
                tactic={tactic}
                selectedAccounts={selectedIds}
                onSelectionChange={onChange}
                onProviderConnect={close}
              />
            </li>
          ))}
        </ul>
      )}
    </DropdownTrigger>
  )
}
